<script setup>
/**
 * FormControlMRegistrantsRegistrantsLogin
 * Secure automated customer session portal simulator button.
 * Pulls relational user access parameters out of remote category bridges,
 * contextually exposing target sign-in routes for entities assigned to credentialed active groups.
 */
import {ref, computed, watch, onMounted} from 'vue'
import Button from '@/common/Button.vue'
import api from '@/services/api'
import {useCategoriesStore} from "@/stores/categories"
import {useItemDataStore} from "@/stores/itemData"
import {useAppStore} from '@/stores/appStore'

const props = defineProps({
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    }
})

const categoriesStore = useCategoriesStore()
const itemDataStore = useItemDataStore()
const appStore = useAppStore()

const categoryIds = ref([])
const hasAccessGroups = ref(false)
const isLoading = ref(false)

const email = computed(() => props.currentData?.username || props.currentData?.email || '')

const showButton = computed(() => hasAccessGroups.value && email.value.trim().length > 0)

const registrantId = computed(() => props.currentData?.id)

/**
 * Synchronous Visibility Adjuster
 * Updates the fields configuration registry properties directly based on state validity flags.
 */
const updateLayoutVisibility = () => {
    if (props.field) {
        props.field.visibility = showButton.value ? '' : 'hidden'
    }
}

/**
 * Sequential Access Evaluator
 * Runs a unified data retrieval sequence to prevent layout race conditions.
 */
const evaluateAccessControl = async () => {
    if (!registrantId.value) {
        categoryIds.value = []
        hasAccessGroups.value = false
        updateLayoutVisibility()
        return
    }

    isLoading.value = true
    try {
        const columnData = await categoriesStore.getTableColumnData({
            tableName: 'm_registrants_registrants',
            columnName: 'contactGroup'
        })

        const activeColumnArray = columnData || []
        const filteredCatIds = activeColumnArray
            .filter(o => o.itemId === registrantId.value)
            .map(o => o.catId)

        categoryIds.value = filteredCatIds

        if (filteredCatIds.length > 0) {
            const preloadedGroups = await itemDataStore.preload({
                tableName: 'm_registrants_groups',
                ids: filteredCatIds,
            })

            const matchingGroupArray = preloadedGroups || []
            hasAccessGroups.value = matchingGroupArray.filter(o => !!o.accessGroup).length > 0
        } else {
            hasAccessGroups.value = false
        }
    } catch (err) {
        console.error('Operational crash validating registrant permission boundaries:', err)
        categoryIds.value = []
        hasAccessGroups.value = false
    } finally {
        isLoading.value = false
        updateLayoutVisibility()
    }
}

/**
 * Requests a temporary cryptographically signed auto-login vector string
 * and securely transfers the administrative session out to the public front-end endpoint.
 */
const handleAutoLogin = async () => {
    if (!registrantId.value) return

    try {
        const response = await api.get('api/customer/get-auto-login-key', {
            params: {customerId: registrantId.value}
        })

        const loginKey = response?.data?.key
        const targetHostEndpoint = appStore.websiteEndpoint

        if (loginKey && targetHostEndpoint) {
            window.open(`${targetHostEndpoint}/controller/Customers/autoLogin/${loginKey}`)
        }
    } catch (err) {
        console.error('Automated authentication processing routing failure:', err)
    }
}

watch(registrantId, async () => {
    await evaluateAccessControl()
})

watch(showButton, () => {
    updateLayoutVisibility()
})

onMounted(async () => {
    await evaluateAccessControl()
})
</script>

<template>
    <div class="form-control-registrants-login-wrapper">

        <Button
            v-if="showButton && !isLoading"
            class="btn-light border btn-sm"
            @click="handleAutoLogin"
        >
            Login
        </Button>

    </div>
</template>

<style scoped>
/* Interface parameters route through global corporate layout designs natively */
</style>